import { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, TextField } from '@mui/material';
import ContextMenu from './ContextMenu';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Tree } from 'react-arborist';

const initialData = [
  {
    id: 'folder-1', name: 'Veckorapporter', children: [
      { id: 'file-1', name: 'Vecka 1.pdf' },
      { id: 'file-2', name: 'Vecka 2.pdf' },
      { id: 'file-3', name: 'Vecka 3.pdf' },
      { id: 'file-4', name: 'Vecka 4.pdf' },
    ]
  },
  {
    id: 'folder-2', name: 'Månadsrapporter', children: [
      { id: 'file-5', name: 'Januari.pdf' },
      { id: 'file-6', name: 'Februari.pdf' },
      { id: 'file-7', name: 'Mars.pdf' },
      { id: 'file-8', name: 'April.pdf' },
    ]
  },
  {
    id: 'folder-3', name: 'Slutrapporter', children: [
      { id: 'file-9', name: 'Wilma_Slutrapport1.pdf' },
    ]
  },
];

const moveNodeToBackend = async (movedNodeIds, newParentId, newIndex) => {
  console.log("[API] moveNodeToBackend:", { movedNodeIds, newParentId, newIndex });
  return new Promise(resolve => setTimeout(resolve, 300));
};

export default function VirtualizedTree({ onSelectFile, containerHeight }) {
  const [data, setData] = useState(initialData);
  const [contextMenu, setContextMenu] = useState(null);
  const [clickedNode, setClickedNode] = useState(null);
  const [copiedNode, setCopiedNode] = useState(null);
  const [treeHeight, setTreeHeight] = useState(0);
  const [editingNodeId, setEditingNodeId] = useState(null);
  const treeRef = useRef(null);
  const isFolder = (node) => Array.isArray(node.children);

  const handleMove = async ({ dragIds, parentId, index }) => {
    setData(prev => {
      const clone = (o) => JSON.parse(JSON.stringify(o));
      const newData = clone(prev);

      const removeNode = (nodes, id) => {
        for (let i = 0; i < nodes.length; i++) {
          if (nodes[i].id === id) return nodes.splice(i, 1)[0];
          if (nodes[i].children) {
            const removed = removeNode(nodes[i].children, id);
            if (removed) return removed;
          }
        }
        return null;
      };

      const findNode = (nodes, id) => {
        for (let node of nodes) {
          if (node.id === id) return node;
          if (node.children) {
            const found = findNode(node.children, id);
            if (found) return found;
          }
        }
        return null;
      };

      const movedNodes = dragIds.map(id => removeNode(newData, id)).filter(Boolean);

      if (parentId === null) {
        newData.splice(index, 0, ...movedNodes);
      } else {
        const parent = findNode(newData, parentId);
        if (parent && Array.isArray(parent.children)) {
          parent.children.splice(index, 0, ...movedNodes);
        }
      }

      return sortFoldersFirst(newData);
    });

    await moveNodeToBackend(dragIds, parentId, index);
  };

  const sortFoldersFirst = (items) => {
    return items.map(item => {
      if (isFolder(item)) {
        const sortedChildren = sortFoldersFirst(item.children || []);
        return {
          ...item,
          children: sortedChildren.sort((a, b) => {
            const aIsFolder = isFolder(a);
            const bIsFolder = isFolder(b);
            if (aIsFolder === bIsFolder) return a.name.localeCompare(b.name, 'sv');
            return aIsFolder ? -1 : 1;
          })
        };
      }
      return item;
    });
  };

  const findParentId = (items, childId, parentId = null) => {
    for (const item of items) {
      if (item.id === childId) return parentId;
      if (item.children) {
        const result = findParentId(item.children, childId, item.id);
        if (result) return result;
      }
    }
    return null;
  };

  const handleContextMenu = (e, nodeData) => {
    e.preventDefault();
    e.stopPropagation();
    setClickedNode(nodeData);
    setContextMenu({ top: e.clientY, left: e.clientX });
  };

  const handleClose = () => setContextMenu(null);
  const handleRename = useCallback((node) => {
    setEditingNodeId(node.id);
  }, []);

const saveRename = (id, newName, suffix = "") => {
  const updateName = (items) => items.map(item => {
    if (item.id === id) return { ...item, name: newName + suffix };
    if (item.children) return { ...item, children: updateName(item.children) };
    return item;
  });

  setData(prev => sortFoldersFirst(updateName([...prev])));
  setEditingNodeId(null);
};

  const handleDelete = useCallback((node) => {
    const remove = (items, id) => items.filter(item => {
      if (item.id === id) return false;
      if (item.children) item.children = remove(item.children, id);
      return true;
    });

    setData(prev => remove([...prev], node.id));
  }, []);

const handleCopy = (node) => setCopiedNode({ ...node });
  const handlePaste = useCallback((target) => {
    if (!copiedNode) return;

    const clone = { ...copiedNode, id: Date.now().toString() };
    const insert = (items, id) => items.map(item => {
      if (item.id === id && item.children) {
        return { ...item, children: [...item.children, clone] };
      }
      if (item.children) {
        return { ...item, children: insert(item.children, id) };
      }
      return item;
    });

    setData(prev => insert([...prev], target.id));
  }, [copiedNode]);

const handleDownload = (node) => {
  const link = document.createElement('a');
  link.href = `/files/${node.name}`;
  link.download = node.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const handleShare = (node) => alert(`Dela ${node.name}`);

const handleSelect = (node) => {
  if (!isFolder(node)) {
    if (onSelectFile) {
      console.log("Selecting file:", `/files/${node.name}`);
      onSelectFile({ url: `/files/${node.name}` });
    }
  }
};

const handleNewFolder = (target) => {
  const isFolderNode = Array.isArray(target.children);
  const targetId = isFolderNode ? target.id : findParentId(data, target.id);

  const folderBaseName = "Ny mapp";
  const collectNames = (items, targetId) => {
    for (const item of items) {
      if (item.id === targetId && item.children) {
        return item.children.map(child => child.name);
      }
      if (item.children) {
        const result = collectNames(item.children, targetId);
        if (result) return result;
      }
    }
    return [];
  };

  const existingNames = collectNames(data, targetId);
  let counter = 0;
  let name = folderBaseName;
  while (existingNames.includes(name)) {
    counter++;
    name = `${folderBaseName} ${counter}`;
  }

  const newFolder = { id: Date.now().toString(), name, children: [] };
  const insertFolder = (items) => items.map(item => {
    if (item.id === targetId && item.children) {
      return { ...item, children: [...item.children, newFolder] };
    }
    if (item.children) {
      return { ...item, children: insertFolder(item.children) };
    }
    return item;
  });

  setData(prev => sortFoldersFirst(insertFolder([...prev])));
  setTimeout(() => setEditingNodeId(newFolder.id), 0);
};

const handleNewFile = (fileType) => (target) => {
  const isFolderNode = Array.isArray(target.children);
  const targetId = isFolderNode ? target.id : findParentId(data, target.id);
  const extension = fileType === "pdf" ? ".pdf" : ".uqd";
  const baseName = "Fil";
  const collectNames = (items, targetId) => {
    for (const item of items) {
      if (item.id === targetId && item.children) {
        return item.children.map(child => child.name);
      }
      if (item.children) {
        const result = collectNames(item.children, targetId);
        if (result) return result;
      }
    }
    return [];
  };

  const existingNames = collectNames(data, targetId);
  let counter = 1;
  let name = `${baseName}${counter}${extension}`;
  while (existingNames.includes(name)) {
    counter++;
    name = `${baseName}${counter}${extension}`;
  }

  const newFile = { id: Date.now().toString(), name };
  const insertFile = (items) => items.map(item => {
    if (item.id === targetId && item.children) {
      return { ...item, children: [...item.children, newFile] };
    }
    if (item.children) {
      return { ...item, children: insertFile(item.children) };
    }
    return item;
  });

  setData(prev => sortFoldersFirst(insertFile([...prev])));
  setTimeout(() => setEditingNodeId(newFile.id), 0);
};

useEffect(() => {
  if (typeof containerHeight === 'number' && containerHeight > 100) {
    const targetHeight = containerHeight;
    setTreeHeight((prev) => Math.abs(prev - targetHeight) > 1 ? targetHeight : prev);
  }
}, [containerHeight]);

function Node({ node, style, dragHandle }) {
  const folder = isFolder(node.data);
  const name = node.data.name;
  const dot = name.lastIndexOf(".");
  const base = dot > 0 ? name.slice(0, dot) : name;
  const suffix = dot > 0 ? name.slice(dot) : "";

  return (
    <Box
      ref={dragHandle}
      style={style}
      onContextMenu={(e) => handleContextMenu(e, node.data)}
      onClick={() => folder ? node.toggle() : handleSelect(node.data)}
      sx={{
        display: 'flex', alignItems: 'center', pl: `${node.level * 24}px`, height: '28px', cursor: 'pointer', userSelect: 'none',
        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
        backgroundColor: node.willReceiveDrop ? '#e3f2fd' : (node.isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent'),
        transition: 'background-color 0.2s ease'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', color: folder ? 'primary.main' : 'text.secondary' }}>
        {folder
          ? (node.isOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />)
          : <DescriptionOutlinedIcon fontSize="small" />}
        {editingNodeId === node.data.id ? (
          <TextField
            size="small"
            autoFocus
            defaultValue={base}
            onBlur={(e) => saveRename(node.data.id, e.target.value, suffix)}
            onKeyDown={(e) => e.key === 'Enter' && saveRename(node.data.id, e.target.value, suffix)}
            sx={{ ml: 1, fontSize: '0.8rem' }}
          />
        ) : (
          <Typography variant="body2" sx={{ ml: 1 }}>{base}<Box component="span" sx={{ opacity: 0.6 }}>{suffix}</Box></Typography>
        )}
      </Box>
    </Box>
  );
};

return (
  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
    {treeHeight > 0 ? (
      <Tree
        ref={treeRef}
        data={data}
        openByDefault={true}
        width="100%"
        height={treeHeight}
        indent={24}
        rowHeight={30}
        overscanCount={5}
        paddingTop={0}
        paddingBottom={0}
        onMove={handleMove}
        disableDrop={({ parentNode }) => parentNode && !isFolder(parentNode.data)}
      >
        {Node}
      </Tree>
    ) : (
      <Box sx={{ p: 2 }}>Laddar rapportträdet...</Box>
    )}
    <ContextMenu
      anchorPosition={contextMenu}
      anchorReference="anchorPosition"
      open={Boolean(contextMenu)}
      onClose={handleClose}
      onRename={handleRename}
      onDelete={handleDelete}
      onCopy={handleCopy}
      onPaste={handlePaste}
      onDownload={handleDownload}
      onShare={handleShare}
      onNewFolder={handleNewFolder}
      onNewFile={handleNewFile}
      targetNode={clickedNode?.id ? clickedNode : null}
    />
  </Box>
);
}
